import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { CATEGORIES, CASTELLI, CONDITIONS } from "../constants";
import { Upload, X, Zap, Euro, Repeat, Star, Shield, MapPin, Camera, Gavel, Tag, AlertTriangle, Car, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { listingsAPI, auctionsAPI, paymentsAPI, uploadImage, resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PostListing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acceptedLiability, setAcceptedLiability] = useState(false);
  const [autoDetails, setAutoDetails] = useState({ brand: "", model: "", year: "", km: "", features: "" });
  const [data, setData] = useState({
    title: "",
    price: "",
    category: "elettronica",
    condition: "Come nuovo",
    castello: user?.castello || "",
    description: "",
    allow_barter: true,
    allow_offer: true,
    featured: false,
    as_auction: false,
    auction_days: 7,
    auction_start_price: "",
    auction_buy_now: "",
  });

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-[#1d3d3a]">Accedi per pubblicare un annuncio</h2>
        <Button className="mt-4 bg-[#e86a24] hover:bg-[#c8561a]" onClick={() => navigate("/login")}>Accedi</Button>
      </div>
    );
  }

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8 - photos.length);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const url = await uploadImage(f);
        urls.push(url);
      }
      setPhotos([...photos, ...urls].slice(0, 8));
    } catch (err) {
      toast.error("Errore upload foto");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const canQuickPublish = photos.length > 0 && data.price && data.castello && data.category;

  const submitListing = async (isQuick) => {
    if (saving) return; // Hard guard: prevent any double submission while a request is in flight
    if (isQuick && !canQuickPublish) {
      toast.error("Foto, prezzo, categoria e castello sono obbligatori");
      return;
    }
    if (!isQuick && (!data.title || !data.price || !data.castello || !data.category)) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (!acceptedLiability) {
      toast.error("Devi accettare la dichiarazione di responsabilità per pubblicare");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: data.title || (isQuick ? `Annuncio in ${data.castello}` : data.title),
        price: Number(data.price),
        category: data.category || "elettronica",
        condition: data.condition,
        castello: data.castello,
        description: data.description,
        images: photos,
        // If auction: barter/offer not applicable on the listing itself
        allow_barter: data.as_auction ? false : data.allow_barter,
        allow_offer: data.as_auction ? false : data.allow_offer,
        // Vetrina is now PAID via Stripe in profile, not free at listing creation.
        featured: false,
        accepted_liability: true,
        ...(data.category === "auto" ? { auto_details: autoDetails } : {}),
      };
      const l = await listingsAPI.create(payload);

      // Business accounts: listing is inactive until €0,50 paid. Pay BEFORE creating auction.
      // Auto listings: payment required (€1/mese) sia per privati che per aziende.
      const needsAutoPayment = data.category === "auto" && l?.payment_required && !l?.active;
      const needsBusinessPayment = user?.account_type === "business" && data.category !== "auto" && l?.payment_required && !l?.active;

      if (data.as_auction) {
        const days = Math.max(1, Math.min(30, Number(data.auction_days) || 7));
        const endsAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
        const startPrice = Number(data.auction_start_price || data.price);
        if (!startPrice || startPrice <= 0) {
          toast.error("Inserisci il prezzo base d'asta");
          setSaving(false);
          return;
        }
        const buyNow = data.auction_buy_now ? Number(data.auction_buy_now) : null;
        if (buyNow !== null && buyNow <= startPrice) {
          toast.error("Il prezzo 'Compralo subito' deve essere maggiore della base d'asta");
          setSaving(false);
          return;
        }
        try {
          await auctionsAPI.create({
            listing_id: l.id,
            start_price: startPrice,
            ends_at: endsAt,
            ...(buyNow !== null ? { buy_now_price: buyNow } : {}),
          });
          toast.success("Asta avviata!", {
            description: `Base €${startPrice}${buyNow ? ` · Compralo subito €${buyNow}` : ""} · ${days}gg`,
          });
          if (needsBusinessPayment) {
            try {
              const isIT = user?.country === "IT";
              const pkgId = isIT ? "business_listing_fee_it" : "business_listing_fee";
              const { url } = await paymentsAPI.createBusinessListingFee(l.id, window.location.origin, pkgId);
              if (url) {
                toast.info(`Reindirizzamento al pagamento (€${isIT ? "1" : "0,50"})…`);
                window.location.href = url;
                return;
              }
            } catch (err) {
              toast.error("Asta creata ma errore checkout. Paga dal profilo per attivarla.");
              setTimeout(() => navigate("/profilo"), 800);
              return;
            }
          }
          setTimeout(() => navigate(`/aste`), 700);
          return;
        } catch (err) {
          toast.error("Annuncio creato ma asta fallita", { description: err?.response?.data?.detail || "Riprova dal dettaglio annuncio" });
          setTimeout(() => navigate(`/annuncio/${l.id}`), 800);
          return;
        }
      }

      toast.success("Annuncio pubblicato!", { description: "Visibile a tutta la Repubblica" });
      // Auto listings: redirect to Stripe €1/mese (both private + business)
      if (needsAutoPayment) {
        try {
          const { url } = await paymentsAPI.createAutoListing(l.id, window.location.origin);
          if (url) {
            toast.info("Reindirizzamento al pagamento Annuncio Auto (€1/mese)…");
            window.location.href = url;
            return;
          }
        } catch (err) {
          toast.error("Annuncio salvato ma errore checkout. Paga dal profilo per pubblicarlo.");
          setTimeout(() => navigate("/profilo"), 800);
          return;
        }
      }
      // Business accounts: redirect to Stripe checkout to pay listing fee (€0,50 SM or €1 IT)
      if (needsBusinessPayment) {
        try {
          const isIT = user?.country === "IT";
          const pkgId = isIT ? "business_listing_fee_it" : "business_listing_fee";
          const { url } = await paymentsAPI.createBusinessListingFee(l.id, window.location.origin, pkgId);
          if (url) {
            toast.info(`Reindirizzamento al pagamento (€${isIT ? "1" : "0,50"})…`);
            window.location.href = url;
            return;
          }
        } catch (err) {
          toast.error("Annuncio salvato ma errore checkout. Paga dal profilo per pubblicarlo.");
          setTimeout(() => navigate("/profilo"), 800);
          return;
        }
      }
      if (data.featured) {
        setTimeout(() => {
          toast.info("Vai sul tuo profilo per promuoverlo in Vetrina Premium con Stripe (€4.99)", { duration: 6000 });
        }, 1200);
      }
      setTimeout(() => navigate(`/annuncio/${l.id}`), 600);
    } catch (err) {
      // If err.response.status >= 400 the listing was NOT created → safe to show error.
      // If error is thrown AFTER the listing was created (e.g. ReferenceError in our own code),
      // we still log it but don't mislead the user with a generic "errore" that would tempt re-submission.
      console.error("submitListing failed:", err);
      const detail = err?.response?.data?.detail;
      if (detail) {
        toast.error("Errore pubblicazione", { description: detail });
      } else {
        // Non-API error → the listing might already be created. Inform user and stop them re-submitting.
        toast.error("Errore di rete", {
          description: "Verifica nel tuo profilo se l'annuncio è già pubblicato prima di riprovare.",
          duration: 8000,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 bg-[#e86a24]/15 text-[#c8561a] px-3 py-1 rounded-full text-xs font-bold mb-2">
          <MapPin className="w-3 h-3"/> Solo Repubblica di San Marino
        </div>
        <h1 className="text-3xl font-black text-[#1d3d3a]">Pubblica il tuo annuncio</h1>
        <p className="text-slate-500 mt-1">Bastano foto, prezzo e castello. In 30 secondi sei online.</p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => setStep(1)} className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 transition-colors ${step === 1 ? "border-[#e86a24] bg-[#e86a24] text-white" : "border-slate-200 bg-white text-slate-700"}`}>
          <Zap className="w-4 h-4 inline mr-1"/> Pubblicazione veloce
        </button>
        <button onClick={() => setStep(2)} className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 transition-colors ${step === 2 ? "border-[#1d3d3a] bg-[#1d3d3a] text-white" : "border-slate-200 bg-white text-slate-700"}`}>
          Annuncio completo
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div>
          <label className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2"><Camera className="w-4 h-4"/>Foto {step===1 && <span className="text-xs bg-orange-100 text-[#e86a24] px-2 py-0.5 rounded">obbligatoria</span>}</label>
          <div className="grid grid-cols-4 gap-3">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200">
                <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
                {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] bg-[#e86a24] text-white px-1.5 py-0.5 rounded font-bold">COPERTINA</span>}
                <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3 h-3"/></button>
              </div>
            ))}
            {photos.length < 8 && (
              <label className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#e86a24] hover:bg-orange-50 transition-colors">
                <Upload className="w-6 h-6 text-slate-400 mb-1"/>
                <span className="text-[11px] text-slate-500 font-medium">{uploading ? "Upload..." : "Aggiungi foto"}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={uploading} />
              </label>
            )}
          </div>
        </div>

        {step === 1 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1"><Euro className="w-4 h-4"/>Prezzo</label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                  <Input data-testid="post-price-input" type="number" value={data.price} onChange={(e) => setData({...data, price: e.target.value})} placeholder="0" className="pl-9 h-11 text-lg font-bold"/>
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1"><MapPin className="w-4 h-4"/>Castello</label>
                <Select value={data.castello} onValueChange={(v) => setData({...data, castello: v})}>
                  <SelectTrigger className="h-11" data-testid="post-castello-select"><SelectValue placeholder="Seleziona castello"/></SelectTrigger>
                  <SelectContent>
                    {CASTELLI.slice(1).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1"><Tag className="w-4 h-4"/>Categoria</label>
                <Select value={data.category} onValueChange={(v) => setData({...data, category: v})}>
                  <SelectTrigger className="h-11" data-testid="post-category-select"><SelectValue placeholder="Scegli categoria"/></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {data.category === "auto" && (
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full mt-2 flex items-center gap-2 bg-sky-50 border border-sky-300 text-sky-900 rounded-xl p-3 text-xs hover:bg-sky-100 transition-colors"
                    data-testid="auto-switch-full"
                  >
                    <Car className="w-4 h-4 shrink-0" />
                    <span className="flex-1 text-left">
                      <strong>Categoria Auto:</strong> passa al form completo per inserire marca, modello, anno e km. <span className="underline font-bold">Clicca qui →</span>
                    </span>
                  </button>
                )}
              </div>
            </div>

            <AuctionBlock data={data} setData={setData} />

            {!data.as_auction && (
              <div className="space-y-2">
                <ToggleRow label="Accetta offerte" description="Gli utenti possono proporre un prezzo diverso" checked={data.allow_offer} onChange={(v) => setData({...data, allow_offer: v})} icon={Euro} />
                <ToggleRow label="Accetta baratto / scambio" description="Gli utenti possono proporre un baratto (con o senza conguaglio in €)" checked={data.allow_barter} onChange={(v) => setData({...data, allow_barter: v})} icon={Repeat} />
              </div>
            )}

            {!data.as_auction && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                <strong>Solo 4 campi!</strong> Foto, prezzo, categoria e castello.
              </div>
            )}
            <LiabilityCheckbox checked={acceptedLiability} onChange={setAcceptedLiability} />
            <Button data-testid="post-submit-quick" onClick={() => submitListing(true)} disabled={!canQuickPublish || saving || !acceptedLiability} className="w-full h-12 bg-[#e86a24] hover:bg-[#c8561a] rounded-xl text-base font-bold disabled:opacity-50">
              {data.as_auction ? <Gavel className="w-5 h-5 mr-2"/> : <Zap className="w-5 h-5 mr-2"/>}
              {saving ? "Pubblicazione..." : data.as_auction ? `Avvia asta (${data.auction_days}gg)` : "Pubblica ora"}
            </Button>
            <MissingFieldsHint
              photos={photos.length}
              price={data.price}
              category={data.category}
              castello={data.castello}
              liability={acceptedLiability}
            />
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <label className="text-sm font-bold text-slate-900 block mb-2">Titolo</label>
              <Input value={data.title} onChange={(e) => setData({...data, title: e.target.value})} placeholder="Es: iPhone 14 Pro 256GB Come Nuovo" className="h-11"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-slate-900 block mb-2">Prezzo</label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                  <Input type="number" value={data.price} onChange={(e) => setData({...data, price: e.target.value})} placeholder="0" className="pl-9 h-11"/>
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-slate-900 block mb-2">Categoria</label>
                <Select value={data.category} onValueChange={(v) => setData({...data, category: v})}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Scegli categoria"/></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-bold text-slate-900 block mb-2">Condizione</label>
                <Select value={data.condition} onValueChange={(v) => setData({...data, condition: v})}>
                  <SelectTrigger className="h-11"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-bold text-slate-900 block mb-2">Castello</label>
                <Select value={data.castello} onValueChange={(v) => setData({...data, castello: v})}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Seleziona castello"/></SelectTrigger>
                  <SelectContent>
                    {CASTELLI.slice(1).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-bold text-slate-900 block mb-2">Descrizione</label>
              <Textarea value={data.description} onChange={(e) => setData({...data, description: e.target.value})} rows={5} placeholder={data.category === "auto" ? "Descrivi la vettura: stato, manutenzioni, optional, motivo della vendita…" : "Descrivi l'oggetto: condizioni, accessori inclusi..."}/>
            </div>

            {data.category === "auto" && (
              <CarDetailsBlock value={autoDetails} onChange={setAutoDetails} />
            )}

            <div className="space-y-3 border-t pt-4">
              <ToggleRow label="Accetta offerte" description="Gli utenti possono proporre un prezzo diverso" checked={data.allow_offer} onChange={(v) => setData({...data, allow_offer: v})} icon={Euro} />
              <ToggleRow label="Accetta baratto" description="Gli utenti possono proporre baratto (con o senza conguaglio)" checked={data.allow_barter} onChange={(v) => setData({...data, allow_barter: v})} icon={Repeat} />
              <ToggleRow label="VETRINA Premium (€4.99 con Stripe)" description="Promuovi l'annuncio in evidenza per 7gg. Pagherai dal tuo profilo dopo la pubblicazione." checked={data.featured} onChange={(v) => setData({...data, featured: v})} icon={Star} accent="amber" />
            </div>

            <AuctionBlock data={data} setData={setData} />

            <LiabilityCheckbox checked={acceptedLiability} onChange={setAcceptedLiability} />

            <Button data-testid="post-submit-full" onClick={() => submitListing(false)} disabled={saving || !acceptedLiability} className="w-full h-12 bg-[#1d3d3a] hover:bg-[#102624] rounded-xl text-base font-bold disabled:opacity-50">
              {data.as_auction ? <Gavel className="w-5 h-5 mr-2"/> : <Shield className="w-5 h-5 mr-2"/>}
              {saving ? "Pubblicazione..." : data.as_auction ? `Avvia asta (${data.auction_days}gg)` : "Pubblica annuncio completo"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, icon: Icon, accent }) {
  const color = accent === "amber" ? "text-amber-600" : "text-emerald-600";
  return (
    <div className="flex items-start justify-between gap-3 p-3 border border-slate-200 rounded-xl">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${color} mt-0.5`}/>
        <div>
          <div className="text-sm font-bold text-slate-900">{label}</div>
          <div className="text-xs text-slate-500">{description}</div>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function LiabilityCheckbox({ checked, onChange }) {
  return (
    <label
      className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer select-none transition-colors ${
        checked
          ? "border-emerald-500 bg-emerald-50"
          : "border-amber-300 bg-amber-50 hover:border-amber-500"
      }`}
      data-testid="liability-checkbox-row"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 rounded border-slate-300 accent-[#e86a24] shrink-0"
        data-testid="liability-checkbox"
      />
      <div className="flex-1 text-xs leading-relaxed">
        <div className="flex items-center gap-1.5 mb-1">
          <AlertTriangle className={`w-4 h-4 ${checked ? "text-emerald-600" : "text-amber-600"}`} />
          <span className="font-black text-[#1d3d3a] text-sm uppercase tracking-wide">Dichiarazione di responsabilità</span>
        </div>
        <p className="text-slate-700">
          <strong>Mi assumo ogni responsabilità sulla merce, marchi e prodotti caricati.</strong>{" "}
          <span className="text-slate-600">SAN MARINO MARKET (sanmarinomarket.com) non si assume alcuna responsabilità sulla merce pubblicata. È <strong className="text-red-700">vietato</strong> l'inserimento di <strong>prodotti contraffatti</strong> all'interno della piattaforma.</span>
        </p>
      </div>
    </label>
  );
}

function MissingFieldsHint({ photos, price, category, castello, liability }) {
  const missing = [];
  if (!photos) missing.push("almeno 1 foto");
  if (!price || Number(price) <= 0) missing.push("prezzo");
  if (!category) missing.push("categoria");
  if (!castello) missing.push("castello");
  if (!liability) missing.push("spunta dichiarazione di responsabilità");

  if (missing.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 text-xs text-emerald-700 font-semibold mt-1" data-testid="post-ready-hint">
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
        Tutto pronto! Clicca <strong>Pubblica ora</strong> per pubblicare.
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-amber-50 border-2 border-amber-300 p-3 text-xs text-amber-900 flex items-start gap-2 mt-1" data-testid="post-missing-hint">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <div>
        <strong className="block mb-0.5">Per pubblicare manca:</strong>
        <ul className="list-disc pl-5 space-y-0.5">
          {missing.map((m) => <li key={m}>{m}</li>)}
        </ul>
      </div>
    </div>
  );
}


function CarDetailsBlock({ value, onChange }) {
  const update = (k, v) => onChange({ ...value, [k]: v });
  const years = Array.from({ length: 40 }, (_, i) => String(new Date().getFullYear() - i));
  return (
    <div className="rounded-2xl border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-blue-50 p-4 space-y-3" data-testid="car-details-block">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-sky-600 text-white flex items-center justify-center">
          <Car className="w-5 h-5" />
        </div>
        <div>
          <div className="font-black text-[#1d3d3a]">Dettagli vettura</div>
          <div className="text-xs text-slate-600">Compila per dare informazioni precise agli acquirenti</div>
        </div>
        <span className="ml-auto inline-flex items-center gap-1 bg-amber-400 text-[#1d3d3a] text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full">
          <CreditCard className="w-3 h-3" /> €1/mese
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FieldLabel label="Marca *">
          <Input value={value.brand} onChange={(e) => update("brand", e.target.value)} placeholder="es. Fiat, BMW, Audi" className="h-10 bg-white" data-testid="auto-brand" />
        </FieldLabel>
        <FieldLabel label="Modello *">
          <Input value={value.model} onChange={(e) => update("model", e.target.value)} placeholder="es. Panda, Serie 3" className="h-10 bg-white" data-testid="auto-model" />
        </FieldLabel>
        <FieldLabel label="Anno immatricolazione *">
          <Select value={value.year} onValueChange={(v) => update("year", v)}>
            <SelectTrigger className="h-10 bg-white" data-testid="auto-year"><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldLabel>
        <FieldLabel label="Chilometri *">
          <Input type="number" min="0" value={value.km} onChange={(e) => update("km", e.target.value)} placeholder="es. 75000" className="h-10 bg-white" data-testid="auto-km" />
        </FieldLabel>
      </div>

      <FieldLabel label="Caratteristiche (motore, alimentazione, cambio, optional)">
        <Textarea
          rows={3}
          value={value.features}
          onChange={(e) => update("features", e.target.value)}
          placeholder="es. Diesel 1.6, cambio automatico, climatizzatore bizona, sensori di parcheggio, cerchi in lega 17''"
          className="bg-white"
          data-testid="auto-features"
        />
      </FieldLabel>

      <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
        <CreditCard className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          La pubblicazione di un annuncio nella categoria <strong>Auto</strong> costa <strong>€1/mese</strong> sia per privati che per aziende.
          Dopo la pubblicazione verrai reindirizzato a Stripe per il pagamento. L'annuncio resta attivo 30 giorni.
        </span>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-700 mb-1 block">{label}</label>
      {children}
    </div>
  );
}


function AuctionBlock({ data, setData }) {
  const options = [
    { v: 1, label: "1 giorno" },
    { v: 3, label: "3 giorni" },
    { v: 7, label: "7 giorni" },
    { v: 14, label: "14 giorni" },
  ];
  return (
    <div
      className={`rounded-2xl border-2 transition-all ${
        data.as_auction
          ? "border-[#e86a24] bg-gradient-to-br from-orange-50 to-amber-50"
          : "border-slate-200 bg-white"
      }`}
      data-testid="auction-block"
    >
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${data.as_auction ? "bg-[#e86a24] text-white" : "bg-slate-100 text-slate-500"}`}>
            <Gavel className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-bold text-slate-900">Metti in asta</div>
              {data.as_auction && (
                <span className="text-[10px] font-black uppercase tracking-wider text-white bg-[#e86a24] px-2 py-0.5 rounded-full">
                  Asta attiva
                </span>
              )}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Il prezzo che hai inserito sarà la <strong>base d'asta</strong>. Gli utenti rilanciano di almeno €10. Chi è in testa allo scadere vince.
            </div>
          </div>
        </div>
        <Switch
          checked={data.as_auction}
          onCheckedChange={(v) => setData({ ...data, as_auction: v })}
          data-testid="auction-toggle"
        />
      </div>

      {data.as_auction && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                <Gavel className="w-3 h-3"/> Prezzo base d'asta *
              </label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-bold">€</span>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={data.auction_start_price}
                  onChange={(e) => setData({ ...data, auction_start_price: e.target.value })}
                  placeholder="es. 50"
                  className="pl-7 h-10 text-sm font-bold border-[#e86a24]/30 bg-white"
                  data-testid="auction-start-price"
                />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Rialzo minimo: €3</div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                <Zap className="w-3 h-3 text-amber-500"/> Compralo subito (opz.)
              </label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-bold">€</span>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={data.auction_buy_now}
                  onChange={(e) => setData({ ...data, auction_buy_now: e.target.value })}
                  placeholder="es. 200"
                  className="pl-7 h-10 text-sm font-bold bg-white"
                  data-testid="auction-buy-now-price"
                />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Paga e chiude subito</div>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 mb-2 block">Durata dell'asta</label>
            <div className="grid grid-cols-4 gap-2">
              {options.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setData({ ...data, auction_days: o.v })}
                  data-testid={`auction-days-${o.v}`}
                  className={`py-2 rounded-lg text-xs font-bold border-2 transition-colors ${
                    data.auction_days === o.v
                      ? "border-[#e86a24] bg-[#e86a24] text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-[#e86a24]/50"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="text-xs text-slate-600 bg-white border border-slate-200 rounded-lg p-3 leading-relaxed">
            <strong className="text-[#1d3d3a]">Riepilogo asta:</strong> base €{data.auction_start_price || "—"}{data.auction_buy_now ? ` · Compralo subito €${data.auction_buy_now}` : ""} · scadenza tra {data.auction_days} {data.auction_days === 1 ? "giorno" : "giorni"} · rialzo minimo €3. Offerte e baratti sono <em>disabilitati</em> sulle aste.
          </div>
        </div>
      )}
    </div>
  );
}
